import { useNavigate } from "flemo";
import { BookOpenIcon, CheckCircle2Icon, ChevronRightIcon, ClipboardPenIcon, PlayIcon } from "lucide-react";
import { useEffect, useRef } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useGenerateExamMutation,
  useLearnedCountsQuery,
  useTargetScoreQuery,
  useTestDatesQuery,
  useWordSetsQuery,
} from "@/hooks/queries";
import { trackEvent } from "@/lib/analytics";
import { TARGET_SCORE_OPTIONS, TestStatus } from "@/lib/constant";

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

type ResumeFlashcards = {
  id: string;
  title: string;
  done: number;
  total: number;
  progress: number;
};

export function StudyCTA() {
  const { push } = useNavigate();
  const { data: testDates = [], isPending: isTestsPending } = useTestDatesQuery();
  const { mutate: generateExam, isPending: isGenerating } = useGenerateExamMutation();
  const { data: targetScore = TARGET_SCORE_OPTIONS[2] } = useTargetScoreQuery();
  const { data: wordSets = [], isPending: isWordSetsPending } = useWordSetsQuery();
  const { data: learnedCounts = {}, isPending: isCountsPending } = useLearnedCountsQuery();
  const trackedImpressions = useRef(new Set<string>());

  const today = toLocalDateString(new Date());
  const todayItem = testDates.find(item => item.date === today);
  const inProgressTest = testDates
    .flatMap(item => [
      ...(item.exam ? [{ id: item.exam.id, status: item.exam.status, date: item.date }] : []),
      ...item.retryExams.map(exam => ({ id: exam.id, status: exam.status, date: item.date })),
    ])
    .find(exam => exam.status === TestStatus.IN_PROGRESS);
  const completedExam = todayItem?.exam?.status === TestStatus.COMPLETED ? todayItem.exam : null;
  const goalAchieved = completedExam ? Number(completedExam.score) >= targetScore : false;

  const resumeFlashcards: ResumeFlashcards | null =
    wordSets
      .map(set => {
        const counts = learnedCounts[set.id];
        const done = counts ? counts.knownCount + counts.learningCount : 0;

        return {
          id: set.id,
          title: set.title,
          done,
          total: set.word_count,
          progress: set.word_count > 0 ? Math.round((done / set.word_count) * 100) : 0,
        };
      })
      .filter(set => set.done > 0 && set.done < set.total)
      .sort((a, b) => b.progress - a.progress)[0] ?? null;

  const isPending = isTestsPending || isWordSetsPending || isCountsPending;
  const dailyType = inProgressTest ? "resume_test" : completedExam ? "view_result" : "new_test";
  const dailyProgress = inProgressTest ? "in_progress" : completedExam ? 100 : 0;

  useEffect(() => {
    if (isPending) {
      return;
    }

    const dailyKey = `${dailyType}:${inProgressTest?.id ?? todayItem?.exam?.id ?? today}`;
    if (!trackedImpressions.current.has(dailyKey)) {
      trackedImpressions.current.add(dailyKey);
      void trackEvent("study_cta_impression", {
        source: "home",
        progress: dailyProgress,
        type: dailyType,
        ...(inProgressTest
          ? { exam_id: inProgressTest.id, exam_date: inProgressTest.date }
          : todayItem?.exam
            ? { exam_id: todayItem.exam.id }
            : {}),
      });
    }

    if (completedExam) {
      const completionKey = `completed:${completedExam.id}`;
      if (!trackedImpressions.current.has(completionKey)) {
        trackedImpressions.current.add(completionKey);
        void trackEvent("daily_study_completion_viewed", {
          source: "home",
          progress: 100,
          exam_id: completedExam.id,
          score: Number(completedExam.score),
          goal_achieved: goalAchieved,
        });
      }
    }

    if (resumeFlashcards) {
      const flashcardKey = `resume_flashcards:${resumeFlashcards.id}:${resumeFlashcards.progress}`;
      if (!trackedImpressions.current.has(flashcardKey)) {
        trackedImpressions.current.add(flashcardKey);
        void trackEvent("study_cta_impression", {
          source: "home",
          progress: resumeFlashcards.progress,
          type: "resume_flashcards",
          word_set_id: resumeFlashcards.id,
        });
      }
    }
  }, [
    completedExam,
    dailyProgress,
    dailyType,
    goalAchieved,
    inProgressTest,
    isPending,
    resumeFlashcards,
    today,
    todayItem?.exam,
  ]);

  function handleDailyClick() {
    if (inProgressTest) {
      void trackEvent("study_cta_clicked", {
        source: "home",
        progress: "in_progress",
        type: "resume_test",
        exam_id: inProgressTest.id,
        exam_date: inProgressTest.date,
      });
      push("/tests/:id", { id: inProgressTest.id });

      return;
    }

    if (completedExam) {
      void trackEvent("study_cta_clicked", {
        source: "home",
        progress: 100,
        type: "view_result",
        exam_id: completedExam.id,
      });
      push("/tests/:id", { id: completedExam.id });

      return;
    }

    void trackEvent("study_cta_clicked", { source: "home", progress: 0, type: "new_test" });
    generateExam(today);
  }

  function handleFlashcardsClick() {
    if (!resumeFlashcards) {
      return;
    }

    void trackEvent("study_cta_clicked", {
      source: "home",
      progress: resumeFlashcards.progress,
      type: "resume_flashcards",
      word_set_id: resumeFlashcards.id,
    });
    push("/flashcards/:id", { id: resumeFlashcards.id });
  }

  if (isPending) {
    return (
      <div className="flex w-full items-center gap-3.5 rounded-xl bg-card p-4">
        <Skeleton className="size-10 shrink-0 rounded-xl" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-52" />
        </div>
      </div>
    );
  }

  const dailyConfig = inProgressTest
    ? {
        icon: PlayIcon,
        iconColor: "text-orange-500 bg-orange-500/10",
        title: "진행 중인 테스트 이어서 풀기",
        description: "중단한 문제부터 바로 계속해요",
      }
    : completedExam
      ? {
          icon: CheckCircle2Icon,
          iconColor: "text-emerald-600 bg-emerald-500/10",
          title: goalAchieved ? "오늘 학습 완료 · 목표 달성!" : "오늘 학습 완료!",
          description: `${completedExam.score}점 · 결과와 오답을 확인해 보세요`,
        }
      : {
          icon: ClipboardPenIcon,
          iconColor: "text-blue-500 bg-blue-500/10",
          title: "오늘의 테스트 시작하기",
          description: "매일 테스트로 실력을 확인해 보세요",
        };
  const DailyIcon = dailyConfig.icon;

  return (
    <div className="flex flex-col gap-2.5" aria-label="오늘 학습 바로가기">
      {completedExam ? (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/8 px-4 py-3">
          <p className="text-xs font-bold text-emerald-700">오늘 할 학습을 모두 마쳤어요</p>
          <p className="mt-0.5 text-xs text-muted-foreground">다음 복습은 스마트 복습에서 · 새 테스트는 내일 만나요</p>
        </div>
      ) : null}

      {resumeFlashcards ? (
        <button
          type="button"
          onClick={handleFlashcardsClick}
          className="flex w-full items-center gap-3.5 rounded-xl border border-secondary/20 bg-card p-4 text-left transition-transform active:scale-[0.98]"
        >
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
            <BookOpenIcon className="size-5" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="truncate text-sm font-semibold text-foreground">플래시카드 이어보기</p>
            <div className="flex items-center gap-2">
              <div className="h-1.5 min-w-16 flex-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-secondary" style={{ width: `${resumeFlashcards.progress}%` }} />
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {resumeFlashcards.title} · {resumeFlashcards.done}/{resumeFlashcards.total}
              </span>
            </div>
          </div>
          <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
        </button>
      ) : null}

      <button
        type="button"
        onClick={handleDailyClick}
        disabled={isGenerating}
        className="flex w-full items-center gap-3.5 rounded-xl bg-card p-4 text-left transition-transform active:scale-[0.98] disabled:opacity-60 disabled:active:scale-100"
      >
        <div className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${dailyConfig.iconColor}`}>
          <DailyIcon className="size-5" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="truncate text-sm font-semibold text-foreground">{dailyConfig.title}</p>
          <p className="text-xs text-muted-foreground">{isGenerating ? "테스트 생성 중…" : dailyConfig.description}</p>
        </div>

        <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
      </button>
    </div>
  );
}
